import { useEffect, useState, type FormEvent } from "react";
import { errorMessage } from "@/components/auth/issues";
import { AppShell } from "@/components/layout/AppShell";
import { Button, Checkbox, TextField } from "@/components/ui/FormControls";
import { InfoTip } from "@/components/ui/InfoTip";
import { api } from "@/lib/api";
import { BRAND } from "@/lib/brand";
import { useAuth, type User } from "@/store/auth";

type Note = { kind: "ok" | "danger"; text: string } | null;

function NoteLine({ note }: { note: Note }) {
  if (!note) return null;
  return (
    <p role={note.kind === "danger" ? "alert" : "status"} className={`note note-${note.kind} text-label`}>
      {note.text}
    </p>
  );
}

const ROLE_LABEL: Record<User["role"], string> = { learner: "학습자", admin: "관리자" };

/** 내 계정 , 프로필 수정, 마케팅 동의 변경, 비밀번호 변경 */
export function AccountPage() {
  const { user } = useAuth();
  if (!user) return null;
  return (
    <AppShell>
      <main className="page max-w-doc">
        <header>
          <h1 className="text-title font-extrabold tracking-tight text-ink">내 계정</h1>
          <p className="mt-label text-caption text-ink-muted">
            <span className="font-mono">{user.email}</span>, {ROLE_LABEL[user.role]}, 가입 {new Date(user.createdAt).toLocaleDateString("ko-KR")}
          </p>
        </header>
        <ProfileCard user={user} />
        <EnterpriseUpgradeCard user={user} />
        <ConsentCard user={user} />
        <PasswordCard />
      </main>
    </AppShell>
  );
}

function EnterpriseUpgradeCard({ user }: { user: User }) {
  const load = useAuth((state) => state.load);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [devCode, setDevCode] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<Note>(null);

  if (user.license === "enterprise") {
    return (
      <section className="admin-card">
        <div><h2 className="admin-card-title">기업용 라이선스</h2>
        <p className="admin-card-desc">기업용 기능을 사용할 수 있습니다. 인증한 회사 이메일은 {user.verifiedCompanyEmail ?? "확인된 주소"}입니다.</p></div>
      </section>
    );
  }

  async function requestCode(e: FormEvent) {
    e.preventDefault();
    setNote(null);
    setBusy(true);
    try {
      const result = await api<{ ok: true; email: string; devCode?: string }>('/auth/enterprise/request', { method: "POST", json: { email: email.trim() } });
      setEmail(result.email);
      setDevCode(result.devCode ?? null);
      setSent(true);
      setNote({ kind: "ok", text: "회사 이메일로 인증번호를 보냈습니다." });
    } catch (err) {
      setNote({ kind: "danger", text: errorMessage(err) });
    } finally {
      setBusy(false);
    }
  }

  async function verifyCode(e: FormEvent) {
    e.preventDefault();
    setNote(null);
    setBusy(true);
    try {
      await api('/auth/enterprise/verify', { method: "POST", json: { email: email.trim(), code: code.trim() } });
      await load();
      setNote({ kind: "ok", text: "회사 이메일 인증이 완료되었습니다. 기업용 기능을 사용할 수 있습니다." });
    } catch (err) {
      setNote({ kind: "danger", text: errorMessage(err) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="admin-card">
      <div><h2 className="admin-card-title">기업용 기능 사용</h2>
      <p className="admin-card-desc">교육용 계정은 회사 이메일을 인증한 뒤 기업용 라이선스로 전환할 수 있습니다. 무료 메일 주소는 사용할 수 없습니다.</p></div>
      <form onSubmit={requestCode} className="auth-form">
        <TextField label="회사 이메일" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@company.com" hint="인증번호를 받을 회사 이메일을 입력해 주세요." />
        <div className="form-actions">
          <Button type="submit" variant="secondary" disabled={busy || !email.trim()}>{busy ? "보내는 중…" : "인증번호 받기"}</Button>
        </div>
      </form>
      {sent && (
        <form onSubmit={verifyCode} className="auth-form">
          <TextField label="인증번호" inputMode="numeric" required value={code} onChange={(e) => setCode(e.target.value)} placeholder="6자리 인증번호" hint="인증번호는 10분 동안 유효합니다." />
          {devCode && <p role="status" className="note note-ok text-label">개발 환경 인증번호: <strong className="font-mono">{devCode}</strong></p>}
          <div className="form-actions">
            <Button type="submit" variant="primary" disabled={busy || code.trim().length !== 6}>{busy ? "확인 중…" : "인증하고 기업용으로 전환"}</Button>
          </div>
        </form>
      )}
      <NoteLine note={note} />
    </section>
  );
}

function ProfileCard({ user }: { user: User }) {
  const { updateProfile } = useAuth();
  const [name, setName] = useState(user.name);
  const [organization, setOrganization] = useState(user.organization);
  const [department, setDepartment] = useState(user.department ?? "");
  const [jobTitle, setJobTitle] = useState(user.jobTitle ?? "");
  const [phone, setPhone] = useState(user.phone);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<Note>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setNote(null);
    if (!name.trim() || !organization.trim() || phone.trim().length < 7) {
      setNote({ kind: "danger", text: "이름, 회사, 연락처는 비울 수 없습니다." });
      return;
    }
    setBusy(true);
    try {
      await updateProfile({ name: name.trim(), organization: organization.trim(), department: department.trim() || null, jobTitle: jobTitle.trim() || null, phone: phone.trim() });
      setNote({ kind: "ok", text: "프로필을 저장했습니다." });
    } catch (err) {
      setNote({ kind: "danger", text: errorMessage(err) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="admin-card">
      <div><h2 className="admin-card-title">프로필</h2>
      <p className="admin-card-desc">이메일은 바꿀 수 없습니다. 나머지는 여기서 고칩니다.</p></div>
      <form onSubmit={submit} className="auth-form">
        <div className="form-grid">
          <TextField label="이메일" type="email" value={user.email} readOnly />
          <TextField label="이름" required value={name} onChange={(e) => setName(e.target.value)} />
          <TextField label="회사 또는 기관명" required value={organization} onChange={(e) => setOrganization(e.target.value)} />
          <TextField label="연락처(휴대전화)" type="tel" required value={phone} onChange={(e) => setPhone(e.target.value)} />
          <TextField label="부서(선택)" value={department} onChange={(e) => setDepartment(e.target.value)} />
          <TextField label="직책(선택)" value={jobTitle} onChange={(e) => setJobTitle(e.target.value)} />
        </div>
        <NoteLine note={note} />
        <div className="form-actions">
          <Button type="submit" variant="primary" disabled={busy}>
            {busy ? "저장 중…" : "프로필 저장"}
          </Button>
        </div>
      </form>
    </section>
  );
}

function ConsentCard({ user }: { user: User }) {
  const { updateProfile } = useAuth();
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<Note>(null);

  async function toggle(next: boolean) {
    setNote(null);
    setBusy(true);
    try {
      await updateProfile({ consentMarketing: next });
      setNote({ kind: "ok", text: next ? "안내 및 마케팅 활용에 동의했습니다." : "동의를 철회했습니다. 더 이상 안내를 보내지 않습니다." });
    } catch (err) {
      setNote({ kind: "danger", text: errorMessage(err) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="admin-card">
      <div><h2 className="admin-card-title">교육 및 서비스 안내 동의</h2>
      <p className="admin-card-desc">선택 사항입니다. 언제든 바꿀 수 있고, 바꾸면 바로 반영됩니다.</p></div>
      <div>
        <Checkbox
          label="교육 및 서비스 안내와 영업, 마케팅 활용 동의"
          checked={user.consentMarketing}
          disabled={busy}
          onChange={(e) => void toggle(e.target.checked)}
          aside={<InfoTip label="어떤 안내를 받나요">{BRAND.DEVELOPER}가 수소 LCA 및 탄소 관련 교육 과정과 서비스를 이메일 또는 연락처로 안내합니다. 동의 여부와 시각은 계정에 기록됩니다.</InfoTip>}
          description="동의하지 않아도 서비스 이용과 수료 관리에는 영향이 없습니다."
        />
      </div>
      <NoteLine note={note} />
    </section>
  );
}

function PasswordCard() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [again, setAgain] = useState("");
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<Note>(null);

  // 성공 안내는 다음 입력이 시작되면 지운다
  useEffect(() => {
    if (note?.kind === "ok" && (current || next || again)) setNote(null);
  }, [current, next, again, note]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setNote(null);
    if (next.length < 8) return setNote({ kind: "danger", text: "새 비밀번호는 8자 이상이어야 합니다." });
    if (next !== again) return setNote({ kind: "danger", text: "새 비밀번호가 서로 다릅니다." });
    if (next === current) return setNote({ kind: "danger", text: "현재 비밀번호와 다른 비밀번호를 정해 주세요." });
    setBusy(true);
    try {
      await api("/auth/password", { method: "POST", json: { current, next } });
      setCurrent("");
      setNext("");
      setAgain("");
      setNote({ kind: "ok", text: "비밀번호를 바꿨습니다." });
    } catch (err) {
      setNote({ kind: "danger", text: errorMessage(err) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="admin-card">
      <h2 className="admin-card-title">비밀번호 변경</h2>
      <form onSubmit={submit} className="auth-form">
        <div className="form-grid">
          <div className="form-span">
            <TextField label="현재 비밀번호" type="password" autoComplete="current-password" required value={current} onChange={(e) => setCurrent(e.target.value)} />
          </div>
          <TextField label="새 비밀번호" type="password" autoComplete="new-password" required minLength={8} value={next} onChange={(e) => setNext(e.target.value)} hint="8자 이상" />
          <TextField label="새 비밀번호 확인" type="password" autoComplete="new-password" required value={again} onChange={(e) => setAgain(e.target.value)} />
        </div>
        <NoteLine note={note} />
        <div className="form-actions">
          <Button type="submit" variant="primary" disabled={busy || !current || !next || !again}>
            {busy ? "변경 중…" : "비밀번호 바꾸기"}
          </Button>
        </div>
      </form>
    </section>
  );
}
